import { useState, useEffect } from 'react';

const BREAKPOINTS = {
  miniscule: 375,
  sm: 640,
  tablet: 920,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * Returns true when the viewport is at least as wide as the given breakpoint.
 * Custom breakpoints: sm: 640px, tablet: 920px, lg: 1024px, xl: 1280px, 2xl: 1536px.
 * SSR-safe: defaults to false until the first client-side measurement.
 */
export function useBreakpoint(bp: Breakpoint): boolean {
  const minWidth = BREAKPOINTS[bp];
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth >= minWidth;
  });

  useEffect(() => {
    const mq = window.matchMedia(
      `(min-width: ${minWidth}px)`
    );
    setMatches(mq.matches);
    const handler = (e: MediaQueryListEvent) =>
      setMatches(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [minWidth]);

  return matches;
}

/**
 * Returns the current breakpoint name based on viewport width.
 * Returns the largest breakpoint that the viewport matches.
 * SSR-safe: defaults to 'sm' until the first client-side measurement.
 */
export function useWhichBreakpoint(): Breakpoint {
  const [currentBreakpoint, setCurrentBreakpoint] =
    useState<Breakpoint>(() => {
      if (typeof window === 'undefined') return 'sm';

      const width = window.innerWidth;
      const breakpoints = Object.entries(BREAKPOINTS).sort(
        ([, a], [, b]) => b - a
      ) as [Breakpoint, number][];

      for (const [bp, minWidth] of breakpoints) {
        if (width >= minWidth) {
          return bp;
        }
      }
      return 'miniscule';
    });

  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      const breakpoints = Object.entries(BREAKPOINTS).sort(
        ([, a], [, b]) => b - a
      ) as [Breakpoint, number][];

      for (const [bp, minWidth] of breakpoints) {
        if (width >= minWidth) {
          setCurrentBreakpoint(bp);
          return;
        }
      }
      setCurrentBreakpoint('miniscule');
    };

    updateBreakpoint();
    window.addEventListener('resize', updateBreakpoint);
    return () =>
      window.removeEventListener(
        'resize',
        updateBreakpoint
      );
  }, []);

  return currentBreakpoint;
}
